// ══════════════════════════════════════════════════════
//  多源交叉识别 — RootData + CoinGecko + DeFiLlama
//  对一个搜索词查找跨源候选，给出最高置信度的匹配
// ══════════════════════════════════════════════════════

import { rdSkill } from './rootdata';
import { cgGet } from './coingecko';

// 名称归一化：去除大小写、空格、标点
function norm(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

// 名称相似度评分（0-1）：精确=1.0，含子串=0.7，包含查询=0.6，否则=0
function nameSimilarity(query, candidate) {
  const q = norm(query);
  const c = norm(candidate);
  if (!q || !c) return 0;
  if (q === c) return 1.0;
  if (c.includes(q) && q.length >= 4) return 0.7;
  if (q.includes(c) && c.length >= 4) return 0.6;
  return 0;
}

/**
 * 跨源识别：返回所有候选的合并/打分列表
 * @param {string} query
 * @returns {Promise<{ candidates: Array, best: object|null }>}
 */
export async function resolveProjectIdentity(query) {
  const qNorm = norm(query);
  if (!qNorm) return { candidates: [], best: null };

  // 并行查询三源
  const [rdRaw, cgRaw, defiRaw] = await Promise.all([
    rdSkill("ser_inv", { query, precise_x_search: false }).catch(() => null),
    cgGet(`/search?query=${encodeURIComponent(query)}`).catch(() => ({ coins: [] })),
    fetch("https://api.llama.fi/protocols").then(r => r.json()).catch(() => []),
  ]);

  // ── 1. RootData 候选 ──
  const rdResults = Array.isArray(rdRaw) ? rdRaw : (rdRaw?.list || rdRaw?.data || []);
  const rdProjects = rdResults.filter(x => x.type === 1).slice(0, 10);
  const rdCandidates = rdProjects.map(x => ({
    source: "rootdata",
    name: x.name || "",
    id: x.id,
    logo: x.logo || "",
    similarity: nameSimilarity(query, x.name),
  })).filter(c => c.similarity > 0);

  // ── 2. CoinGecko 候选 ──
  const cgCoins = (cgRaw?.coins || []).slice(0, 10);
  const cgCandidates = cgCoins.map(c => ({
    source: "coingecko",
    name: c.name || "",
    id: c.id,
    symbol: c.symbol,
    logo: c.large || c.thumb || "",
    similarity: nameSimilarity(query, c.name),
  })).filter(c => c.similarity > 0);

  // ── 3. DeFiLlama 候选 ──
  const defiProtocols = Array.isArray(defiRaw) ? defiRaw : [];
  const defiCandidates = defiProtocols
    .map(p => ({
      source: "defillama",
      name: p.name || "",
      id: p.slug,
      symbol: p.symbol,
      similarity: nameSimilarity(query, p.name),
    }))
    .filter(c => c.similarity > 0)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 5);

  const allCandidates = [...rdCandidates, ...cgCandidates, ...defiCandidates];

  // ── 4. 按归一化名称分组合并跨源 ──
  // 历史 bug：ser_inv "Pharos" 返回 3 个 type=1 都叫 "Pharos"（不同 id），
  //   逐个 `grouped[key].sources[c.source] = c` 覆盖后只剩最后一条，
  //   通常是 hot_index 最低的，导致 best.sources.rootdata.id 拿到错 Pharos。
  // 修复：同名同源场景采用 first-wins —— ser_inv 默认按 hot 排序，第一条最热门。
  const grouped = {};
  for (const c of allCandidates) {
    const key = norm(c.name);
    if (!grouped[key]) {
      grouped[key] = {
        name: c.name,
        sources: {},
        maxSimilarity: 0,
      };
    }
    if (!grouped[key].sources[c.source]) {
      grouped[key].sources[c.source] = c;
    }
    grouped[key].maxSimilarity = Math.max(grouped[key].maxSimilarity, c.similarity);
  }

  // ── 5. 综合评分 = 名称相似度 × 0.5 + 源数量加成 × 0.5 ──
  // 历史问题：单源精确（如 RootData 有旧 "Pharos"）→ similarity 1.0 × 0.7 + 0.33 × 0.3 = 0.80
  //         三源部分（如新 "Pharos Network" RWA L1 跨 RD+CG+DL）→ 0.7 × 0.7 + 1.0 × 0.3 = 0.79
  //         单源精确反而赢，劫持用户意图。提高 sourceBonus 权重让多源信号更可信。
  const merged = Object.values(grouped).map(g => {
    const sourceCount = Object.keys(g.sources).length;
    const sourceBonus = Math.min(sourceCount / 3, 1); // 1源=0.33, 2源=0.67, 3源=1.0
    const confidence = g.maxSimilarity * 0.5 + sourceBonus * 0.5;
    return {
      name: g.name,
      sources: g.sources,
      sourceCount,
      similarity: g.maxSimilarity,
      confidence,
    };
  }).sort((a, b) => b.confidence - a.confidence);

  return {
    candidates: merged.slice(0, 5),
    best: merged[0] || null,
  };
}
